import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { reportScrapingFailure } from "../../../../util/scrapingFailures";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  line-height: 1;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #333;
  }

  &:focus {
    outline: 2px solid #2e7d32;
    outline-offset: 2px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #212121;
`;

const ExplanationText = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #757575;
  line-height: 1.5;
`;

const ExamplesList = styled.ul`
  margin: 0 0 16px 0;
  padding-left: 20px;
  font-size: 14px;
  color: #757575;
  line-height: 1.5;
`;

const InstructionsBox = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px;
  margin: 0 0 20px 0;
`;

const InstructionsTitle = styled.div`
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InstructionsText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #212121;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: 2px solid #2e7d32;
    outline-offset: 2px;
    border-color: #2e7d32;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: 2px solid #2e7d32;
    outline-offset: 2px;
    border-color: #2e7d32;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #2e7d32;
  color: white;

  &:hover:not(:disabled) {
    background-color: #1b5e20;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #f5f5f5;
  color: #212121;

  &:hover:not(:disabled) {
    background-color: #e0e0e0;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 13px;
  margin-top: 8px;
`;

const FailureReportModal = ({
  isOpen,
  onClose,
  restaurantName,
  restaurantWebsite,
  scrapingMetadata,
  onReportSubmitted,
  setAlert,
  hideMenuUrl = false,
}) => {
  const [menuUrl, setMenuUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate menu URL if provided
    if (menuUrl.trim() && !isValidUrl(menuUrl.trim())) {
      setError("Please enter a valid URL");
      return;
    }

    setIsSubmitting(true);

    try {
      const reportData = {
        restaurantName,
        restaurantWebsite,
        menuUrl: menuUrl.trim() || null,
        notes: notes.trim() || null,
        scrapingMetadata: scrapingMetadata || {},
      };

      const result = await reportScrapingFailure(reportData);

      if (onReportSubmitted) {
        onReportSubmitted(result);
      }

      // Show success message
      if (setAlert) {
        if (result.jobId) {
          setAlert("Report submitted. Parsing menu...", "success");
        } else {
          setAlert("Report submitted successfully", "success");
        }
      }

      // Reset form and close modal
      setMenuUrl("");
      setNotes("");
      onClose();
    } catch (err) {
      console.error("Error submitting report:", err);
      setError(
        err.response?.data?.error ||
          "Failed to submit report. Please try again."
      );
      if (setAlert) {
        setAlert("Failed to submit report. Please try again.", "failure");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      // Try adding https:// if no protocol
      try {
        const url = new URL("https://" + string);
        return true;
      } catch (_) {
        return false;
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalClose onClick={onClose} aria-label="Close modal">
          ×
        </ModalClose>
        <ModalTitle>{hideMenuUrl ? "Report Issue" : "Report Processing Failure"}</ModalTitle>
        {hideMenuUrl ? (
          <ExplanationText>
            Something look wrong with this result? Let us know what the issue is.
          </ExplanationText>
        ) : (
          <>
            <ExplanationText>
              The menu scraping/parsing failed. If you have a direct link to the
              menu, you can provide it here.
            </ExplanationText>
            <ExamplesList>
              <li>https://restaurant.com/menu</li>
              <li>https://restaurant.com/menu.pdf</li>
              <li>https://restaurant.com/menu-image.jpg</li>
            </ExamplesList>
            <InstructionsBox>
              <InstructionsTitle>Important:</InstructionsTitle>
              <InstructionsText>
                Please provide a direct URL to the menu page. This should be a
                direct link to the menu (HTML page, PDF, or Image). Not the
                restaurant's homepage - the actual menu page.
              </InstructionsText>
            </InstructionsBox>
          </>
        )}
        <form onSubmit={handleSubmit}>
          {!hideMenuUrl && (
            <FormGroup>
              <Label htmlFor="menuUrl">
                Menu URL (HTML, PDF, or Image) {menuUrl.trim() && "*"}
              </Label>
              <Input
                id="menuUrl"
                type="text"
                placeholder="https://restaurant.com/menu"
                value={menuUrl}
                onChange={(e) => setMenuUrl(e.target.value)}
                disabled={isSubmitting}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label htmlFor="notes">Notes (optional)</Label>
            <TextArea
              id="notes"
              placeholder="Any additional information about the failure..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <ButtonGroup>
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {hideMenuUrl
                ? "Report Issue"
                : menuUrl.trim()
                  ? "Report & Parse Menu"
                  : "Report Issue"}
            </PrimaryButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default FailureReportModal;




